 
import { AutoFocusTarget } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { isPlatformServer } from '@angular/common';
import {
  Binding,
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  PLATFORM_ID,
  Signal,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDrawerMode, MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { SideSheetSide } from '@libs/common/models';
import { filter } from 'rxjs';
import { SideSheetService } from './side-sheet.service';

@Component({
  selector: 'ui-side-sheet',
  imports: [
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
  ],
  templateUrl: './side-sheet.component.html',
  styleUrl: './side-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideSheetComponent {
  private readonly defaultSideSheetMode: MatDrawerMode = 'over';
  private readonly sideSheetService: SideSheetService = inject(SideSheetService);
  private readonly router: Router = inject(Router);
  private readonly defaultSideSheetWidth: string = '360px';
  private readonly defaultAutoFocusTarget: AutoFocusTarget = 'first-tabbable';
  public isServerPlatform: boolean = isPlatformServer(inject<string>(PLATFORM_ID));

  public leftSideOpen: WritableSignal<boolean> = signal(false);
  public leftSideMode: WritableSignal<MatDrawerMode> = signal(this.defaultSideSheetMode);
  public leftSideWidth: WritableSignal<string> = signal(this.defaultSideSheetWidth);
  public leftSideAutoFocusTarget: WritableSignal<AutoFocusTarget> = signal(this.defaultAutoFocusTarget);

  public rightSideOpen: WritableSignal<boolean> = signal(false);
  public rightSideMode: WritableSignal<MatDrawerMode> = signal(this.defaultSideSheetMode);
  public rightSideWidth: WritableSignal<string> = signal(this.defaultSideSheetWidth);
  public rightSideAutoFocusTarget: WritableSignal<AutoFocusTarget> = signal(this.defaultAutoFocusTarget);

  public leftSideContent: Signal<ViewContainerRef> = viewChild.required('leftSideContent', { read: ViewContainerRef });
  public rightSideContent: Signal<ViewContainerRef> = viewChild.required('rightSideContent', { read: ViewContainerRef });

  public constructor() {
    this.sideSheetService.createSideSheetWithComponent$.pipe(takeUntilDestroyed()).subscribe((data) => {
      this.createComponentAndOpenSideSheet(data.side, data.componentCtr, data.inputs, data.mode, data.width, data.autoFocusTarget, data.injector);
    });

    this.sideSheetService.createSideSheetWithTemplate$.pipe(takeUntilDestroyed()).subscribe((data) => {
      this.createTemplateAndOpenSideSheet(data.side, data.template, data.mode, data.width, data.autoFocusTarget);
    });

    this.sideSheetService.closeSideSheet$.pipe(takeUntilDestroyed()).subscribe((side) => {
      if (side === 'left') {
        this.leftSideOpen.set(false);
      } else {
        this.rightSideOpen.set(false);
      }
    });

    this.router.events.pipe(takeUntilDestroyed(),
      filter(event => event instanceof NavigationEnd),
    ).subscribe(() => {
      this.leftSideOpen.set(false);
      this.rightSideOpen.set(false);
    });
  }

  private createComponentAndOpenSideSheet<T>(
    side: SideSheetSide,
    component: ComponentType<T>,
    inputs: Binding[],
    mode: MatDrawerMode = this.defaultSideSheetMode,
    width: string = this.defaultSideSheetWidth,
    autoFocusTarget: AutoFocusTarget = this.defaultAutoFocusTarget,
    injector?: Injector): void {

    if (side === 'left') {
      this.leftSideMode.set(mode);
      this.leftSideWidth.set(width);
      this.leftSideAutoFocusTarget.set(autoFocusTarget);

      this.leftSideContent().clear();
      this.leftSideContent().createComponent(component, { injector: injector, bindings: inputs });
      this.leftSideOpen.set(true);
    } else {
      this.rightSideMode.set(mode);
      this.rightSideWidth.set(width);
      this.rightSideAutoFocusTarget.set(autoFocusTarget);

      this.rightSideContent().clear();
      this.rightSideContent().createComponent(component, { injector: injector, bindings: inputs });
      this.rightSideOpen.set(true);
    }
  }

  private createTemplateAndOpenSideSheet(
    side: SideSheetSide,
    view: TemplateRef<unknown>,
    mode: MatDrawerMode = this.defaultSideSheetMode,
    width: string = this.defaultSideSheetWidth,
    autoFocusTarget: AutoFocusTarget = this.defaultAutoFocusTarget,
  ): void {

    if (side === 'left') {
      this.leftSideMode.set(mode);
      this.leftSideWidth.set(width);
      this.leftSideAutoFocusTarget.set(autoFocusTarget);

      this.leftSideContent().clear();
      this.leftSideContent().createEmbeddedView(view);
      this.leftSideOpen.set(true);
    } else {
      this.rightSideMode.set(mode);
      this.rightSideWidth.set(width);
      this.rightSideAutoFocusTarget.set(autoFocusTarget);

      this.rightSideContent().clear();
      this.rightSideContent().createEmbeddedView(view);
      this.rightSideOpen.set(true);
    }
  }
}
